import { z } from "zod";

/**
 * 取数表达式 DSL（`extraction-expression-v1`）。
 *
 * 这是**数据**，不是源码：模型只能从这些枚举里挑操作，本地执行器只解释已实现的分支。
 * 因此 `eval` / `new Function` / 函数体 / `window` 路径在类型层就不存在——不是"记得别执行"。
 * 见统一技术实施方案 §5.2、§5.3。
 */

export const EXTRACTION_DSL_VERSION = "extraction-expression-v1" as const;

/**
 * 配额在契约里冻结：执行器与模型两侧读同一组数字。
 * 表达式字节上限按 UTF-8 计算（中文选择器按字符算会超预算）。
 */
export const EXTRACTION_LIMITS = {
  maxExpressionBytes: 8_192,
  maxSelectorChars: 1_200,
  maxPathChars: 800,
  maxItems: 200,
  maxCharsPerItem: 2_000,
  maxTotalChars: 16_000,
  maxEntities: 200,
  maxCompoundSelectors: 12,
  maxMatchIterations: 5_000,
  maxTransformChain: 6,
} as const;

/** §5.3：属性读取采用允许列表；`data-*` 不得成为任意隐藏数据出口。 */
export const ALLOWED_READ_ATTRIBUTES = [
  "href",
  "src",
  "alt",
  "title",
  "datetime",
  "content",
  "aria-label",
  "aria-valuenow",
  "aria-valuetext",
  "lang",
] as const;

export const ExtractionErrorCodeSchema = z.enum([
  "EXPRESSION_REJECTED",
  "EXPRESSION_UNSAFE",
  "EXTRACTION_LIMIT",
  "SELECTOR_OUT_OF_SCOPE",
  "TARGET_AMBIGUOUS",
  "NO_MATCH",
]);
export type ExtractionErrorCode = z.infer<typeof ExtractionErrorCodeSchema>;

/* -------------------------------------------------------------------------- */
/* Selector：role/label 语义选择器、受限 CSS、受限相对 XPath、表格行列映射        */
/* -------------------------------------------------------------------------- */

const NameMatchSchema = z.string().trim().min(1).max(160);

export const ExtractionSelectorSchema = z.discriminatedUnion("kind", [
  /** 语义优先：role + 可访问名。 */
  z.object({
    kind: z.literal("role"),
    role: z.string().trim().min(1).max(60),
    name: NameMatchSchema.optional(),
    exact: z.boolean().default(false),
    index: z.number().int().nonnegative().max(EXTRACTION_LIMITS.maxItems).optional(),
  }).strict(),
  /** 关联 label 的控件。 */
  z.object({
    kind: z.literal("label"),
    label: NameMatchSchema,
    exact: z.boolean().default(false),
    index: z.number().int().nonnegative().max(EXTRACTION_LIMITS.maxItems).optional(),
  }).strict(),
  /** 受限 CSS：禁用成本不可控的结构匹配，见 validateCssSelector。 */
  z.object({
    kind: z.literal("css"),
    selector: z.string().trim().min(1).max(EXTRACTION_LIMITS.maxSelectorChars),
  }).strict(),
  /** 受限相对 XPath：只允许留在 scope 内的轴，见 validateXPathScope。 */
  z.object({
    kind: z.literal("xpath"),
    path: z.string().trim().min(1).max(EXTRACTION_LIMITS.maxPathChars),
    /** 显式标记相对：绝对路径会被 validator 拒绝。 */
    relative: z.literal(true),
  }).strict(),
  /** 表格行列映射：按表头名取列，不按偶然列序。 */
  z.object({
    kind: z.literal("table_map"),
    tableRef: z.string().trim().min(1).max(160),
    column: z.string().trim().min(1).max(160),
    headerRow: z.number().int().min(1).max(10).default(1),
  }).strict(),
]);
export type ExtractionSelector = z.infer<typeof ExtractionSelectorSchema>;

/* -------------------------------------------------------------------------- */
/* read：只读页面明示值                                                         */
/* -------------------------------------------------------------------------- */

export const ExtractionReadSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), /** 无标签折线只有 trend，不给精确值 */ mode: z.enum(["text_content", "inner_text"]).default("inner_text") }).strict(),
  /** 允许列表外的属性（尤其 data-*）在这里就被拒绝。 */
  z.object({ kind: z.literal("attribute"), name: z.enum(ALLOWED_READ_ATTRIBUTES) }).strict(),
  /** 非敏感表单值；私密字段过滤在采集端另行拦截。 */
  z.object({ kind: z.literal("value") }).strict(),
  z.object({ kind: z.literal("checked") }).strict(),
  z.object({ kind: z.literal("selected_options") }).strict(),
]);
export type ExtractionRead = z.infer<typeof ExtractionReadSchema>;

/* -------------------------------------------------------------------------- */
/* transform：枚举化转换，禁止任意函数体                                          */
/* -------------------------------------------------------------------------- */

/** 只允许项目内线性时间匹配器支持的子集，见 isLinearTimePattern。 */
export const LinearPatternSchema = z.string().min(1).max(200);

export const ExtractionTransformSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("trim") }).strict(),
  z.object({ kind: z.literal("normalize_space") }).strict(),
  z.object({ kind: z.literal("lowercase") }).strict(),
  z.object({ kind: z.literal("strip_currency") }).strict(),
  z.object({ kind: z.literal("parse_number"), locale: z.enum(["en", "zh", "auto"]).default("auto") }).strict(),
  z.object({ kind: z.literal("parse_date"), format: z.enum(["iso", "ymd", "mdy", "auto"]).default("auto") }).strict(),
  z.object({ kind: z.literal("match"), pattern: LinearPatternSchema, group: z.number().int().nonnegative().max(9).default(0) }).strict(),
  z.object({ kind: z.literal("take_first_n"), n: z.number().int().min(1).max(EXTRACTION_LIMITS.maxItems) }).strict(),
]);
export type ExtractionTransform = z.infer<typeof ExtractionTransformSchema>;

/* -------------------------------------------------------------------------- */
/* expect / limit / 整表达式                                                    */
/* -------------------------------------------------------------------------- */

export const ExtractionExpectationSchema = z.object({
  type: z.enum(["string", "number", "date", "boolean", "string_list"]).default("string"),
  unit: z.string().trim().min(1).max(40).optional(),
  /** 匹配数量断言：多义目标不能自动取第一个。 */
  matchCount: z.enum(["exactly_one", "at_least_one", "any"]).default("at_least_one"),
  requiredFields: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  min: z.number().optional(),
  max: z.number().optional(),
  allowEmpty: z.boolean().default(false),
}).strict().superRefine((value, context) => {
  if (value.min !== undefined && value.max !== undefined && value.min > value.max) {
    context.addIssue({ code: "custom", path: ["min"], message: "min 不能大于 max" });
  }
});
export type ExtractionExpectation = z.infer<typeof ExtractionExpectationSchema>;

export const ExtractionLimitSchema = z.object({
  maxItems: z.number().int().min(1).max(EXTRACTION_LIMITS.maxItems).default(50),
  maxCharsPerItem: z.number().int().min(1).max(EXTRACTION_LIMITS.maxCharsPerItem).default(600),
  maxTotalChars: z.number().int().min(1).max(EXTRACTION_LIMITS.maxTotalChars).default(8_000),
}).strict();
export type ExtractionLimit = z.infer<typeof ExtractionLimitSchema>;

export const ExtractionExpressionSchema = z.object({
  version: z.literal(EXTRACTION_DSL_VERSION),
  /** 必须是本次快照已出现的数据区域 ID；执行器不接受快照里没有的 ref。 */
  scopeRef: z.string().trim().min(1).max(160),
  selector: ExtractionSelectorSchema,
  read: ExtractionReadSchema,
  transforms: z.array(ExtractionTransformSchema).max(EXTRACTION_LIMITS.maxTransformChain).default([]),
  expect: ExtractionExpectationSchema.optional(),
  limit: ExtractionLimitSchema,
  /** 实体归属：商品/评论等按真实 ID 关联，不按列表偶然顺序拼接。 */
  entityKey: z.string().trim().min(1).max(80).optional(),
  /** 可选的子字段映射：一行多列时逐列取名。 */
  fields: z.array(z.object({
    field: z.string().trim().min(1).max(80),
    selector: ExtractionSelectorSchema,
    read: ExtractionReadSchema,
    transforms: z.array(ExtractionTransformSchema).max(EXTRACTION_LIMITS.maxTransformChain).default([]),
  }).strict()).max(24).default([]),
}).strict();
export type ExtractionExpression = z.infer<typeof ExtractionExpressionSchema>;

/* -------------------------------------------------------------------------- */
/* 安全校验：纯函数，本地执行器与单测共用                                        */
/* -------------------------------------------------------------------------- */

/**
 * 任一出现即拒绝。这些字符串本身不能表达"读取页面明示值"，
 * 但足以表达任意代码执行或越权读取，所以按拒绝处理而不是按白名单过滤。
 */
/**
 * 代码执行与越权读取的标记。刻意用**带上下文的片段**而不是裸词：
 * `prototype` / `constructor` 也可能是普通 CSS 类名的一部分，
 * 裸词拒绝会把安全的页面结构一起挡掉，而带点号的属性访问才是真正危险的那一种。
 */
const FORBIDDEN_TOKENS = [
  "eval(", "eval (", "new function", "newfunction", "javascript:", "vbscript:",
  "<script", "</script", "window.", "globalThis", "document.cookie",
  "localstorage", "sessionstorage", "indexeddb", "process.env",
  "__proto__", ".constructor", ".prototype", "import(", "require(",
  "fetch(", "xmlhttprequest",
] as const;

/** 去掉转义字符后的扫描序列，避免 `\(` 被当成分组。 */
function unescapedChars(pattern: string): string[] {
  const chars: string[] = [];
  for (let index = 0; index < pattern.length; index += 1) {
    if (pattern[index] === "\\") {
      index += 1;
      continue;
    }
    chars.push(pattern[index]!);
  }
  return chars;
}

/** 组内出现"一个分支是另一个分支的前缀"时，量词叠加会退化（(a|aa)+ 型）。 */
function hasPrefixOverlap(pattern: string): boolean {
  const group = /\(([^)]*)\)\s*[+*{]/.exec(pattern);
  if (!group) return false;
  const alternatives = group[1]!.split("|").map((part) => part.trim()).filter(Boolean);
  if (alternatives.length < 2) return false;
  return alternatives.some((left) => alternatives.some((right) => left !== right && (right.startsWith(left) || left.startsWith(right))));
}

/** 只允许线性时间匹配模式：嵌套量词、反向引用、环视与前缀重叠交替一律拒绝（ReDoS）。 */
export function isLinearTimePattern(pattern: string): boolean {
  if (pattern.length === 0 || pattern.length > 200) return false;
  if (/\\[1-9]|\\k<|\(\?<|\(\?[=!]/.test(pattern)) return false; // 反向引用 / 环视
  if (/\([^)]*[+*][^)]*\)\s*[+*{]/.test(pattern)) return false; // (a+)+ 型嵌套量词
  if (hasPrefixOverlap(pattern)) return false;
  if ((pattern.match(/\.\*/g) ?? []).length > 2) return false; // 多个 .* 叠加会退化成平方级
  let depth = 0;
  for (const char of unescapedChars(pattern)) {
    if (char === "(") depth += 1;
    if (char === ")") depth -= 1;
    if (depth > 4) return false;
  }
  return depth === 0;
}

/** CSS：禁用不受控结构匹配与通用选择器，并限制复合层数。 */
export function validateCssSelector(selector: string): { ok: true } | { ok: false; reason: string } {
  if (!selector.trim()) return { ok: false, reason: "选择器为空" };
  if (selector.length > EXTRACTION_LIMITS.maxSelectorChars) return { ok: false, reason: "选择器超长" };
  if (/(^|[\s,>+~(])\*/.test(selector)) return { ok: false, reason: "不允许通用选择器 *" };
  if (/:has\(/i.test(selector)) return { ok: false, reason: "不允许 :has()" };
  if (/:nth-(?:last-)?child\([^)]*\bof\b/i.test(selector)) return { ok: false, reason: "不允许 :nth-child(... of ...)" };
  if (/:not\(\s*:not\(/i.test(selector)) return { ok: false, reason: "不允许嵌套 :not()" };
  if (/::/.test(selector)) return { ok: false, reason: "不允许伪元素" };
  if ((selector.match(/[>+~]/g) ?? []).length > EXTRACTION_LIMITS.maxCompoundSelectors) return { ok: false, reason: "复合选择器层数超限" };
  return { ok: true };
}

/** XPath：只允许留在 scope 内的轴；绝对路径与祖先/前驱轴一律拒绝。 */
export function validateXPathScope(path: string): { ok: true } | { ok: false; reason: string } {
  const trimmed = path.trim();
  if (!trimmed) return { ok: false, reason: "路径为空" };
  if (trimmed.length > EXTRACTION_LIMITS.maxPathChars) return { ok: false, reason: "路径超长" };
  if (trimmed.startsWith("/")) return { ok: false, reason: "不允许绝对路径或根路径" };
  if (/(^|[^.\w])(ancestor|ancestor-or-self|parent|preceding|preceding-sibling|following|following-sibling)::/.test(trimmed)) {
    return { ok: false, reason: "不允许越出 scope 的轴" };
  }
  if (/(^|[^.\w])\.\.(?![.\w])/.test(trimmed)) return { ok: false, reason: "不允许父级 .. 节点测试" };
  if (/\bdocument\s*\(|\b(?:window|globalThis)\b/i.test(trimmed)) return { ok: false, reason: "不允许文档级表达式" };
  return { ok: true };
}

function hasForbiddenToken(value: string): string | null {
  const lowered = value.toLowerCase();
  for (const token of FORBIDDEN_TOKENS) if (lowered.includes(token)) return token;
  return null;
}

/** 递归找出表达式里所有字符串，逐个做禁用词与正则安全检查。 */
function inspectStrings(node: unknown, visit: (value: string) => string | null, path = "$"): string | null {
  if (typeof node === "string") return visit(node);
  if (Array.isArray(node)) {
    for (const [index, item] of node.entries()) {
      const issue = inspectStrings(item, visit, `${path}[${index}]`);
      if (issue) return issue;
    }
    return null;
  }
  if (node && typeof node === "object") {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      const issue = inspectStrings(value, visit, `${path}.${key}`);
      if (issue) return issue;
    }
  }
  return null;
}

export type ExtractionValidation =
  | { ok: true; value: ExtractionExpression }
  | { ok: false; code: ExtractionErrorCode; reason: string };

/**
 * 校验入口：先做字节与禁用词等结构性检查，再按 Schema 解析，最后做选择器/正则安全校验。
 * 返回错误码而不是抛异常，模型可以在剩余预算内据此提出修正（§10.4）。
 */
export function validateExtractionExpression(raw: unknown): ExtractionValidation {
  let serialized: string;
  try {
    serialized = JSON.stringify(raw) ?? "";
  } catch {
    return { ok: false, code: "EXPRESSION_REJECTED", reason: "表达式不可序列化" };
  }
  if (new TextEncoder().encode(serialized).length > EXTRACTION_LIMITS.maxExpressionBytes) {
    return { ok: false, code: "EXTRACTION_LIMIT", reason: "表达式超出字节上限" };
  }
  const unsafe = inspectStrings(raw, (value) => {
    const token = hasForbiddenToken(value);
    return token ? `出现禁用内容：${token}` : null;
  });
  if (unsafe) return { ok: false, code: "EXPRESSION_UNSAFE", reason: unsafe };

  const parsed = ExtractionExpressionSchema.safeParse(raw);
  if (!parsed.success) {
    return { ok: false, code: "EXPRESSION_REJECTED", reason: parsed.error.issues[0]?.message ?? "表达式不符合 DSL" };
  }
  const expression = parsed.data;

  for (const transform of collectTransforms(expression)) {
    if (transform.kind === "match" && !isLinearTimePattern(transform.pattern)) {
      return { ok: false, code: "EXPRESSION_UNSAFE", reason: "只允许线性时间匹配模式" };
    }
  }
  for (const selector of collectSelectors(expression)) {
    if (selector.kind === "css") {
      const result = validateCssSelector(selector.selector);
      if (!result.ok) return { ok: false, code: "SELECTOR_OUT_OF_SCOPE", reason: result.reason };
    }
    if (selector.kind === "xpath") {
      const result = validateXPathScope(selector.path);
      if (!result.ok) return { ok: false, code: "SELECTOR_OUT_OF_SCOPE", reason: result.reason };
    }
  }
  return { ok: true, value: expression };
}

export function collectSelectors(expression: ExtractionExpression): ExtractionSelector[] {
  return [expression.selector, ...expression.fields.map((field) => field.selector)];
}

export function collectTransforms(expression: ExtractionExpression): ExtractionTransform[] {
  return [...expression.transforms, ...expression.fields.flatMap((field) => field.transforms)];
}
